/**
 * SensitivityAnalysis — tornado chart + sensitivity table.
 *
 * For each assumption, varies ±20% while holding others constant,
 * measures impact on target metric. All client-side, instant.
 */

import { useMemo, useState } from 'react'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ReferenceLine,
  ResponsiveContainer, Cell,
} from 'recharts'
import { cn } from '../../lib/utils'
import { project, type BaseActuals, type Assumptions, type ProjectionResult } from '../../services/projectionEngine'

interface CatalogEntry {
  assumption_key: string
  display_name: string
  unit: string
  default_value: number
  min_value: number
  max_value: number
}

interface SensitivityAnalysisProps {
  assumptions: Assumptions
  catalog: CatalogEntry[]
  baseActuals: BaseActuals
  className?: string
}

const TARGET_METRICS = [
  { value: 'net_income', label: 'Net Income', path: (r: ProjectionResult) => r.pl.net_income },
  { value: 'gross_profit', label: 'Gross Profit', path: (r: ProjectionResult) => r.pl.gross_profit },
  { value: 'operating_income', label: 'Operating Income', path: (r: ProjectionResult) => r.pl.operating_income },
  { value: 'ending_cash', label: 'Ending Cash', path: (r: ProjectionResult) => r.cf.ending_cash },
  { value: 'working_capital', label: 'Working Capital', path: (r: ProjectionResult) => r.bs.working_capital },
] as const

const LEVELS = [-0.20, -0.10, 0, 0.10, 0.20]
const LEVEL_LABELS = ['-20%', '-10%', 'Base', '+10%', '+20%']

function formatCompact(v: number): string {
  const abs = Math.abs(v)
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${(v / 1_000).toFixed(0)}K`
  return v.toFixed(0)
}

function formatDelta(v: number): string {
  if (Math.abs(v) < 0.5) return '-'
  const sign = v > 0 ? '+' : ''
  return `${sign}$${formatCompact(v)}`
}

export function SensitivityAnalysis({
  assumptions, catalog, baseActuals, className,
}: SensitivityAnalysisProps) {
  const [targetKey, setTargetKey] = useState('net_income')
  const target = TARGET_METRICS.find(t => t.value === targetKey) || TARGET_METRICS[0]

  const sensitivity = useMemo(() => {
    const baseline = project(baseActuals, assumptions)
    const baselineValue = target.path(baseline)

    const entries = (catalog.length > 0 ? catalog : FALLBACK_CATALOG).map(entry => {
      const key = entry.assumption_key as keyof Assumptions
      const currentVal = assumptions[key] ?? entry.default_value

      const results = LEVELS.map(pct => {
        const varied = { ...assumptions, [key]: currentVal * (1 + pct) }
        const proj = project(baseActuals, varied)
        const value = target.path(proj)
        return { pct, value, delta: value - baselineValue }
      })

      const minDelta = Math.min(...results.map(r => r.delta))
      const maxDelta = Math.max(...results.map(r => r.delta))

      return {
        key,
        label: entry.display_name,
        unit: entry.unit,
        currentValue: currentVal,
        results,
        range: Math.abs(maxDelta) + Math.abs(minDelta),
        minDelta,
        maxDelta,
      }
    }).sort((a, b) => b.range - a.range)

    return entries
  }, [assumptions, catalog, baseActuals, target])

  const tornadoData = sensitivity.map(s => ({
    name: s.label,
    downside: s.results[0].delta,
    upside: s.results[4].delta,
  }))

  const chartHeight = Math.max(200, sensitivity.length * 32 + 40)

  return (
    <div className={cn('flex flex-col gap-5', className)}>
      {/* Target selector */}
      <div className="flex items-center gap-3">
        <span className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-hint,#475569)]">
          Target metric
        </span>
        <select
          value={targetKey}
          onChange={e => setTargetKey(e.target.value)}
          className={cn(
            'appearance-none bg-[var(--bg-elevated,var(--bg-elevated))] border border-white/10',
            'rounded-md px-2.5 py-1 text-[11px]',
            'text-[var(--text-primary,#F0F2F5)]',
            'focus:outline-none focus:border-[var(--accent,var(--accent))]',
          )}
        >
          {TARGET_METRICS.map(m => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
      </div>

      {/* Tornado chart */}
      <div>
        <p className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)] mb-2">
          Impact on {target.label} (sorted by sensitivity)
        </p>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            data={tornadoData}
            layout="vertical"
            margin={{ top: 0, right: 30, left: 0, bottom: 0 }}
          >
            <XAxis
              type="number"
              tick={{ fontSize: 10, fill: 'var(--text-muted, #64748B)' }}
              tickFormatter={v => `$${formatCompact(v)}`}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={130}
              tick={{ fontSize: 11, fill: 'var(--text-secondary, #94A3B8)' }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              cursor={false}
              contentStyle={{
                background: 'var(--bg-elevated, var(--bg-elevated))',
                border: '1px solid var(--border-default, #1E3A5F)',
                borderRadius: 6,
                fontSize: 11,
                color: 'var(--text-primary, #F0F2F5)',
              }}
              formatter={(value: any, name: any) => [
                `$${formatCompact(Number(value) || 0)}`,
                name === 'downside' ? '-20% scenario' : '+20% scenario',
              ]}
            />
            <ReferenceLine x={0} stroke="var(--border-default, #1E3A5F)" strokeDasharray="3 3" />
            <Bar dataKey="downside" stackId="range" barSize={16}>
              {tornadoData.map((_, i) => (
                <Cell key={i} fill="#EF4444" fillOpacity={0.7} />
              ))}
            </Bar>
            <Bar dataKey="upside" stackId="range" barSize={16}>
              {tornadoData.map((_, i) => (
                <Cell key={i} fill="#10B981" fillOpacity={0.7} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Sensitivity table */}
      <div>
        <p className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)] mb-2">
          Sensitivity matrix
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-white/[0.06]">
                <th className="text-left py-2 pr-4 text-[10px] uppercase tracking-wide text-[var(--text-hint,#475569)] font-medium">
                  Assumption
                </th>
                {LEVEL_LABELS.map(l => (
                  <th key={l} className="text-right py-2 px-3 text-[10px] uppercase tracking-wide text-[var(--text-hint,#475569)] font-medium">
                    {l}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sensitivity.map(s => (
                <tr
                  key={s.key}
                  className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors"
                >
                  <td className="py-2 pr-4 text-[11px] text-[var(--text-secondary,#94A3B8)]">
                    {s.label}
                  </td>
                  {s.results.map((r, i) => {
                    const isBase = i === 2
                    const color = isBase
                      ? 'var(--text-hint, #475569)'
                      : r.delta > 0
                        ? '#10B981'
                        : r.delta < 0
                          ? '#EF4444'
                          : 'var(--text-hint, #475569)'
                    return (
                      <td
                        key={i}
                        className="text-right py-2 px-3 font-['JetBrains_Mono',monospace]"
                        style={{ color }}
                      >
                        {formatDelta(r.delta)}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

/** Fallback catalog when API is unavailable */
const FALLBACK_CATALOG: CatalogEntry[] = [
  { assumption_key: 'revenue_growth_rate', display_name: 'Revenue Growth', unit: '%', default_value: 5, min_value: -20, max_value: 50 },
  { assumption_key: 'cogs_pct_of_revenue', display_name: 'COGS % of Revenue', unit: '%', default_value: 65, min_value: 20, max_value: 90 },
  { assumption_key: 'opex_growth_rate', display_name: 'OpEx Growth', unit: '%', default_value: 3, min_value: -10, max_value: 30 },
  { assumption_key: 'headcount_change', display_name: 'Headcount Change', unit: '', default_value: 0, min_value: -50, max_value: 100 },
  { assumption_key: 'cost_per_head', display_name: 'Cost per Head', unit: '$', default_value: 75000, min_value: 30000, max_value: 200000 },
  { assumption_key: 'collection_days', display_name: 'Collection Days', unit: 'days', default_value: 45, min_value: 15, max_value: 120 },
  { assumption_key: 'payment_terms', display_name: 'Payment Terms', unit: 'days', default_value: 30, min_value: 10, max_value: 90 },
  { assumption_key: 'interest_rate', display_name: 'Interest Rate', unit: '%', default_value: 5.5, min_value: 0, max_value: 15 },
  { assumption_key: 'tax_rate', display_name: 'Tax Rate', unit: '%', default_value: 25, min_value: 0, max_value: 40 },
  { assumption_key: 'capex', display_name: 'CapEx', unit: '$', default_value: 0, min_value: 0, max_value: 5000000 },
  { assumption_key: 'depreciation', display_name: 'Depreciation', unit: '$', default_value: 0, min_value: 0, max_value: 2000000 },
]
